import React, { createElement } from "react";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";
import { PRINT_ORIENTATION } from "@miplatform/constants";
import { getNestedValue } from "@miplatform/utils";
import { Box, useTheme } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { MiPrintSection } from "./MiPrintSection";
import { MiPrintTable } from "./MiPrintTable";
import { MiPrintTemplate } from "./MiPrintTemplate";
import { MiPrintTemplateExtended } from "./MiPrintTemplateExtended";

export const usePrint = (account) => {
  const theme = useTheme();

  /**
   * Generate DataGrid JSX.
   * @param {Array} data - The data to be displayed in the DataGridPro.
   * @param {Array} columns - Column definitions for the DataGridPro.
   * @param {Object} settings - Additional settings for the DataGridPro.
   * @param {Object} [settings.gridProps] - Additional props for the DataGridPro.
   * @returns {JSX.Element} - The generated JSX for the DataGridPro.
   */
  const getTableJSX = (data, columns, settings) => {
    return (
      <MiPrintTable
        data={data}
        columns={columns}
        account={account}
        settings={settings}
      />
    );
  };

  /**
   * Generate DataGrid JSX.
   * @param {React.ReactNode} jsx - The custom JSX to be printed.
   * @param {Object} settings - Additional settings for the DataGridPro.
   * @param {Object} [settings.gridProps] - Additional props for the DataGridPro.
   * @returns {JSX.Element} - The generated JSX for the DataGridPro.
   */
  const getCustomJsx = (jsx, settings) => {
    return (
      <MiPrintTemplate
        hideWatermark
        hideFooter
        account={account}
        orientation={settings?.orientation}
      >
        <Box padding={6}>{jsx}</Box>
      </MiPrintTemplate>
    );
  };

  /**
   * Generate Pages JSX.
   * @param {Array} pages - The data to be displayed in the templates.
   * @param {Array} template - Template to render the pages.
   * @param {Object} settings - Optional additional settings for the template.
   * @param {Object} [settings.templateProps] - Optional additional props for the component.
   * @param {Object} [settings.headerFields] - Fields to be rendered in the header of the template.
   * @returns {JSX.Element[]} - The generated JSX for the templates.
   */
  const getPagesJSX = (pages, template, settings) => {
    const getHeaderFields = (data) =>
      settings?.headerFields?.map(({ label, field }) => ({
        label,
        value: getNestedValue(data, field),
      }));

    return (
      <>
        {(Array.isArray(pages) ? pages : [pages]).map((page, i) => (
          <MiPrintTemplate
            key={i}
            account={account}
            orientation={settings?.orientation}
            headerFields={getHeaderFields(page)}
          >
            {createElement(template, { ...page, ...settings?.templateProps })}
          </MiPrintTemplate>
        ))}
      </>
    );
  };

  /**
   * Generate Sections JSX.
   * @param {Array} data - The data to be displayed in the sections.
   * @param {Array} template - Template to render the pages.
   * @param {Object} settings - Optional additional settings for the template.
   * @param {Object} [settings.templateProps] - Optional additional props for the component.
   * @param {Object} [settings.highlightedHeaderField] - Optional field to highlight in the header of the template.
   * @param {Object} [settings.headerFields] - Fields to be rendered in the header of the template.
   * @returns {JSX.Element[]} - The generated JSX for the templates.
   */
  const getSectionsJSX = (data, sections, settings) => {
    const getHeaderFields = (page) =>
      settings?.headerFields?.map(
        ({ label, field = "", valueGetter, defaultValue = "-" }) => ({
          label,
          value: valueGetter
            ? valueGetter(page)
            : getNestedValue(page, field) || defaultValue,
        })
      );

    const renderSections = (page) =>
      sections.map(
        ({ title, width, columns, rowTables, tables, ...props }, index) => (
          <MiPrintSection
            key={index}
            title={title}
            width={width}
            columns={
              columns?.map((rows) =>
                rows
                  .filter(
                    ({ shouldRender }) => !shouldRender || shouldRender(page)
                  )
                  .map(
                    ({
                      label,
                      field = "",
                      defaultValue = "-",
                      valueGetter,
                    }) => ({
                      label,
                      value: valueGetter
                        ? valueGetter(page)
                        : getNestedValue(page, field) || defaultValue,
                    })
                  )
              ) || []
            }
            rowTables={
              rowTables?.map((table) =>
                table.map(
                  ({
                    field = "",
                    defaultValue = "",
                    label,
                    valueGetter,
                    ...props
                  }) => ({
                    label,
                    value: valueGetter
                      ? valueGetter(page)
                      : getNestedValue(page, field) || defaultValue,
                    ...props,
                  })
                )
              ) || []
            }
            tables={
              tables?.map(({ field = "", columns }) => ({
                field,
                columns: columns.map(({ label, ...props }) => ({
                  ...props,
                  valueGetter: undefined,
                  field: label,
                  headerName: label,
                })),
                rows: (getNestedValue(page, field) || []).map((item) =>
                  columns.map(
                    ({
                      field = "",
                      defaultValue = "",
                      label,
                      valueGetter,
                      ...props
                    }) => ({
                      label,
                      value: valueGetter
                        ? valueGetter(item)
                        : getNestedValue(item, field) || defaultValue,
                      ...props,
                    })
                  )
                ),
              })) || []
            }
            {...props}
          />
        )
      );

    return (
      <>
        {(Array.isArray(data) ? data : [data]).map((page, i) => (
          <MiPrintTemplate
            key={i}
            account={account}
            orientation={settings?.orientation}
            headerFields={getHeaderFields(page)}
            highlightedHeaderField={
              settings?.highlightedHeaderField
                ? {
                    label: settings?.highlightedHeaderField.label,
                    value: settings?.highlightedHeaderField.valueGetter
                      ? settings?.highlightedHeaderField.valueGetter(page)
                      : getNestedValue(
                          page,
                          settings?.highlightedHeaderField.field
                        ) || "-",
                  }
                : null
            }
            statusHeaderField={
              settings?.statusHeaderField
                ? {
                    label: settings?.statusHeaderField.label,
                    value: settings?.statusHeaderField.valueGetter
                      ? settings?.statusHeaderField.valueGetter(page)
                      : getNestedValue(
                          page,
                          settings?.statusHeaderField.field
                        ) || "-",
                  }
                : null
            }
          >
            <Box
              display="flex"
              flexWrap="wrap"
              justifyContent="space-between"
              gap={2}
            >
              {renderSections(page)}
            </Box>
          </MiPrintTemplate>
        ))}
      </>
    );
  };

  /**
   * Generate extended template JSX for variable-length content with repeating header/footer.
   * @param {React.ReactNode} jsx - The custom JSX content to be printed across as many pages as needed.
   * @param {Object} settings - Optional additional settings for the template.
   * @param {Object} [settings.headerFields] - Fields to be rendered in the header of the template.
   * @param {Object} [settings.highlightedHeaderField] - Optional field to highlight in the header of the template.
   * @param {Object} [settings.statusHeaderField] - Optional status field to render in the header.
   * @param {Boolean} [settings.showPrintedDate] - Optional flag to show the printed date in the header.
   * @returns {JSX.Element} - The generated JSX for the extended template.
   */
  const getExtendedTemplateJSX = (jsx, settings) => {
    return (
      <MiPrintTemplateExtended
        account={account}
        headerFields={settings?.headerFields || []}
        highlightedHeaderField={settings?.highlightedHeaderField || null}
        statusHeaderField={settings?.statusHeaderField || null}
        hideWatermark={settings?.hideWatermark || false}
        showPrintedDate={settings?.showPrintedDate || false}
      >
        {jsx}
      </MiPrintTemplateExtended>
    );
  };

  /**
   * Open the print dialog for a DataGridPro component.
   * @param {Array} data - The data to be displayed in the DataGridPro.
   * @param {Array} columns - Column definitions for the DataGridPro.
   * @param {Object} settings - Optional additional settings for the DataGridPro.
   * @param {Array} [settings.customColumns] - Optional custom column definitions.
   * @param {Object} [settings.gridProps] - Optional additional props for the DataGridPro.
   * @param {Object} [settings.orientation] - Optional setting the orientation of the print.
   */
  const handlePrintTable = (data, columns, settings) =>
    handlePrint(getTableJSX(data, columns, settings), settings);

  /**
   * Open the print dialog for a DataGridPro component.
   * @param {Array} data - The data to be displayed in the templates.
   * @param {Array} template - Template to render the pages.
   * @param {Object} settings - Optional additional settings for the template.
   * @param {Object} [settings.templateProps] - Optional additional props for the component.
   * @param {Object} [settings.highlightedHeaderField] - Optional field to highlight in the header of the template.
   * @param {Object} [settings.headerFields] - Fields to be rendered in the header of the template.
   * @param {Object} [settings.orientation] - Optional setting the orientation of the print.
   */
  const handlePrintPages = (data, template, settings) =>
    handlePrint(getPagesJSX(data, template, settings), settings);

  /**
   * Open the print dialog for a DataGridPro component.
   * @param {Array} data - The data to be displayed in the templates.
   * @param {Array} sections - Sections to be rendered in the pages.
   * @param {Object} settings - Optional additional settings for the template.
   * @param {Object} [settings.headerFields] - Fields to be rendered in the header of the template.
   * @param {Object} [settings.orientation] - Optional setting the orientation of the print.
   */
  const handlePrintSectionsPages = (data, sections, settings) =>
    handlePrint(getSectionsJSX(data, sections, settings), settings);

  /**
   * Open the print dialog for a DataGridPro component.
   * @param {React.ReactNode} jsx - The custom JSX to be printed.
   * @param {Object} settings - Optional additional settings for the template.
   * @param {Object} [settings.headerFields] - Fields to be rendered in the header of the template.
   * @param {Object} [settings.orientation] - Optional setting the orientation of the print.
   */
  const handlePrintCustomJsx = (jsx, settings) =>
    handlePrint(getCustomJsx(jsx, settings), settings);

  /**
   * Open the print dialog for variable-length content with repeating header/footer.
   * @param {React.ReactNode} jsx - The custom JSX to be printed.
   * @param {Object} settings - Optional additional settings for the template.
   * @param {Object} [settings.headerFields] - Fields to be rendered in the header of the template.
   * @param {Object} [settings.highlightedHeaderField] - Optional field to highlight in the header of the template.
   * @param {Object} [settings.statusHeaderField] - Optional status field to render in the header.
   * @param {Object} [settings.orientation] - Optional setting the orientation of the print.
   * @param {Boolean} [settings.showPrintedDate] - Optional flag to show the printed date in the header.
   * @param {Boolean} [settings.hideWatermark] - Optional flag to hide the watermark in the template.
   */
  const handlePrintExtendedTemplate = (jsx, settings) =>
    handlePrint(getExtendedTemplateJSX(jsx, settings), settings);

  /**
   * Open the print dialog for content.
   * @param {React.ReactNode} printContent - The content to be printed.
   * @param {Object} settings - Optional additional settings for the print.
   */
  const handlePrint = (printContent, settings) => {
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    iframe.onload = () => {
      const { document: iframeDocument } = iframe.contentWindow;
      const printContainer = document.createElement("div");
      document.body.appendChild(printContainer);
      const root = createRoot(printContainer);
      flushSync(() => {
        root.render(
          <ThemeProvider theme={theme}>{printContent}</ThemeProvider>
        );
      });
      document.body.removeChild(printContainer);
      const styleSheets = Array.from(document.styleSheets)
        .map((styleSheet) => {
          try {
            return Array.from(styleSheet.cssRules)
              .map((rule) => rule.cssText)
              .join("\n");
          } catch (error) {
            return "";
          }
        })
        .join("\n");

      iframeDocument.open();
      iframeDocument.write(`
      <html>
        <head>
          <style>${styleSheets}</style>
          <style type="text/css" media="print">
              body {
                background-color: ${theme.palette.common.white} !important;
              }
              @page {
                margin: 0;
                ${
                  settings?.orientation === PRINT_ORIENTATION.PORTRAIT
                    ? "size: letter portrait; orientation: portrait;"
                    : ""
                }
                ${
                  settings?.orientation === PRINT_ORIENTATION.LANDSCAPE
                    ? "size: letter landscape; orientation: landscape;"
                    : ""
                }
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }

              * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
          </style>
        </head>
        <body>${printContainer.outerHTML}</body>
      </html>
    `);
      iframeDocument.close();
      const images = iframeDocument.querySelectorAll("img");
      let loadedImages = 0;
      const handleDocumentPrint = () => {
        iframe.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
          root.unmount();
        });
      };

      if (images.length === 0) {
        handleDocumentPrint();
        return;
      }

      const markImageAsLoaded = () => {
        loadedImages++;
        if (loadedImages === images.length) {
          handleDocumentPrint();
        }
      };

      images.forEach((img) => {
        if (img.complete) {
          loadedImages++;
        } else {
          img.onload = markImageAsLoaded;
          img.onerror = markImageAsLoaded;
        }
      });

      if (loadedImages === images.length) {
        handleDocumentPrint();
      }
    };

    iframe.src = "about:blank";
  };

  return {
    handlePrintCustomJsx,
    handlePrintExtendedTemplate,
    handlePrintTable,
    handlePrintPages,
    handlePrintSectionsPages,
  };
};
